// Remembers, per tour, whether a user has seen it - so a tour pops up on its own only while it is still useful.
//
// STORED IN localStorage FOR NOW: this is per browser, so a second computer shows the tour again. When the backend has a
// place for per-user settings, replace readRecord / writeRecord below; nothing else needs to change.

export interface FeatureTourRecord {
    // the tour's version when it was last shown; bumping a tour's version shows it again to everyone
    version: number;
    // how many times it has popped up on its own (a manual replay does not count)
    autoShowCount: number;
    // the user finished the tour, or picked "Don't show this again"
    isDone: boolean;
}

// A tour that was closed without finishing pops up again next time, but not for ever.
export const MAX_FEATURE_TOUR_AUTO_SHOWS = 3;

// The switch for turning every automatic pop-up off from the backend, without touching the manual help buttons.
// Wire the backend setting in here (for example an Application Setting read once at sign-in) - it is the only place that decides.
export const isFeatureTourAutoStartEnabled = (): boolean => true;

// Only one tour runs at a time: a tour that would pop up on its own checks this first and waits for another visit.
let isAnyFeatureTourOpen = false;
export const isFeatureTourOpen = (): boolean => isAnyFeatureTourOpen;
export const setFeatureTourOpen = (isOpen: boolean) => {
    isAnyFeatureTourOpen = isOpen;
};

const STORAGE_PREFIX = 'rvFeatureTour:';

const readRecord = (tourId: string): FeatureTourRecord | null => {
    try {
        const storedText = window.localStorage.getItem(STORAGE_PREFIX + tourId);
        return storedText ? (JSON.parse(storedText) as FeatureTourRecord) : null;
    } catch {
        return null;
    }
};

const writeRecord = (tourId: string, record: FeatureTourRecord) => {
    try {
        window.localStorage.setItem(STORAGE_PREFIX + tourId, JSON.stringify(record));
    } catch {
        // storage unavailable (private window, quota) - the tour may show again next visit, which is harmless
    }
};

// The record that counts for this version of the tour (an older version is treated as never seen).
const readCurrentRecord = (tourId: string, version: number): FeatureTourRecord => {
    const record = readRecord(tourId);
    return record && record.version === version ? record : { version, autoShowCount: 0, isDone: false };
};

export const shouldAutoStartFeatureTour = (tourId: string, version: number): boolean => {
    if (!isFeatureTourAutoStartEnabled()) {
        return false;
    }
    const record = readCurrentRecord(tourId, version);
    return !record.isDone && record.autoShowCount < MAX_FEATURE_TOUR_AUTO_SHOWS;
};

// Call when the tour pops up on its own.
export const recordFeatureTourAutoShown = (tourId: string, version: number) => {
    const record = readCurrentRecord(tourId, version);
    writeRecord(tourId, { ...record, autoShowCount: record.autoShowCount + 1 });
};

// Call when the tour closes: isDone = it was finished, or the user asked not to see it again.
export const recordFeatureTourClosed = (tourId: string, version: number, isDone: boolean) => {
    const record = readCurrentRecord(tourId, version);
    if (isDone && !record.isDone) {
        writeRecord(tourId, { ...record, isDone: true });
    }
};
